import { useState } from "react";
import PremiumHeader from "@/components/premium/PremiumHeader";
import SEO from "@/components/SEO";
import PremiumFooter from "@/components/premium/PremiumFooter";
import StickyContactBar from "@/components/premium/StickyContactBar";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const images = [
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/75r1b3hg_IMG_6451.jpeg", alt: "Exterior", category: "Exterior" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/fx9mf4s6_IMG_7085.jpeg", alt: "Screened Lounge", category: "Outdoor Living" },
  { src: "https://customer-assets.emergentagent.com/job_0254b5f2-ccc0-452f-a975-e862c02dbf90/artifacts/anzmkzaa_IMG_7032.jpeg", alt: "Living Room", category: "Living" },
  { src: "https://customer-assets.emergentagent.com/job_0254b5f2-ccc0-452f-a975-e862c02dbf90/artifacts/jj2004yt_IMG_7028.jpeg", alt: "Entertainment", category: "Living" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/chqzc9wm_IMG_7079.jpeg", alt: "Arcade & Pinball", category: "Game Room" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/qq50qpvb_IMG_7072.jpeg", alt: "Connect Four & Darts", category: "Game Room" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/yw3ssr2w_IMG_6435.png", alt: "Master Bedroom", category: "Bedrooms" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/u8jz9fxx_IMG_6439.png", alt: "Bunk Room", category: "Bedrooms" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/6rbywnux_IMG_7066.jpeg", alt: "Master Bathroom", category: "Bathrooms" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/vixbvlme_IMG_7065.jpeg", alt: "Spa Walk-In Shower", category: "Bathrooms" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/h9u1av3a_IMG_7064.jpeg", alt: "Master Bath Entry", category: "Bathrooms" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/zmvqeh9k_IMG_7062.jpeg", alt: "Marble Water Closet", category: "Bathrooms" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/5dkhrymj_IMG_6437.png", alt: "Rain Shower", category: "Bathrooms" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/q0k9lef0_IMG_6431.png", alt: "Kitchen", category: "Kitchen" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/hvpxfu46_IMG_6433.png", alt: "Dining", category: "Kitchen" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/gtwix1qx_IMG_7060.jpeg", alt: "Aviator Bedroom — King Bed", category: "Bedrooms" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/04f3zcs1_IMG_7056.jpeg", alt: "Full Laundry Room", category: "Laundry" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/onn5i5f9_IMG_7098.jpeg", alt: "Hot Tub on Screened Patio", category: "Outdoor" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/kiamofi0_IMG_7083.jpeg", alt: "Hot Tub & Ping Pong", category: "Outdoor" },
  { src: "https://customer-assets.emergentagent.com/job_beach-escape-fl/artifacts/fd2hugzi_IMG_7088.jpeg", alt: "Tiki Bar & Putting Green", category: "Outdoor" },
];

const GalleryPage = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = "auto";
  };

  const nextImage = () => setCurrentIndex((prev) => (prev + 1) % images.length);
  const prevImage = () => setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <SEO
        title="Photo Gallery | Paradise Beach Rental — Indialantic Beach House Florida"
        description="Tour our luxury Indialantic beach house: game room with arcade machines, private hot tub, spa bathrooms, fenced backyard and more — just 3 minutes from the Atlantic Ocean."
        path="/gallery"
      />
      <PremiumHeader />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-16 bg-[#0A1628]">
          <div className="container-premium text-center">
            <div className="gold-line mx-auto mb-6" />
            <span className="label-sm text-[#C9A962]">Photo Gallery</span>
            <h1 className="headline-xl text-white mt-4 mb-6">Tour the Residence</h1>
            <p className="body-lg text-white/70 max-w-2xl mx-auto">
              Explore every corner of your potential home. Click any image to enlarge.
            </p>
          </div>
        </section>

        {/* Gallery Grid */}
        <section className="section-lg">
          <div className="container-premium">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.05 }}
                  className="relative aspect-square overflow-hidden cursor-pointer group"
                  onClick={() => openLightbox(index)}
                  data-testid={`gallery-thumb-${index}`}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 pointer-events-none"
                  />
                  <div className="absolute inset-0 bg-[#0A1628]/0 group-hover:bg-[#0A1628]/30 transition-colors pointer-events-none" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-[#0A1628]/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                    <p className="text-white text-sm font-body">{image.alt}</p>
                    <p className="text-white/60 text-xs">{image.category}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <PremiumFooter />
      <StickyContactBar />

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[#0A1628]/95 z-[60] flex items-center justify-center"
            onClick={closeLightbox}
            data-testid="gallery-lightbox"
          >
            <button onClick={closeLightbox} className="absolute top-6 right-6 text-white/80 hover:text-white" data-testid="lightbox-close-btn">
              <X className="w-8 h-8" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); prevImage(); }} className="absolute left-4 md:left-8 text-white/80 hover:text-white" data-testid="lightbox-prev-btn">
              <ChevronLeft className="w-10 h-10" />
            </button>
            <button onClick={(e) => { e.stopPropagation(); nextImage(); }} className="absolute right-4 md:right-8 text-white/80 hover:text-white" data-testid="lightbox-next-btn">
              <ChevronRight className="w-10 h-10" />
            </button>
            <motion.img
              key={currentIndex}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              src={images[currentIndex].src}
              alt={images[currentIndex].alt}
              className="max-w-[90vw] max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white text-center">
              <p className="font-body text-sm">{images[currentIndex].alt}</p>
              <p className="text-white/60 text-xs">{currentIndex + 1} / {images.length}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default GalleryPage;
